import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkDeleteClients,
  createClient,
  createContact,
  createResource,
  deleteClient,
  deleteContact,
  deleteResource,
  getClient,
  getClientInvoices,
  listClients,
  seedClientsFromInvoices,
  updateClient,
  updateContact,
  type ClientFilters,
  type ClientInput,
  type ContactInput,
  type ResourceCategory,
} from '../lib/clientsApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

const CLIENTS_KEY = ['clients'] as const;

// --- Queries ---------------------------------------------------------------

export function useClients(filters: ClientFilters = {}) {
  // Global firm switcher (multi-company): threaded internally so pages don't
  // have to pass it. An explicit filters.companyId wins.
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: ClientFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: [...CLIENTS_KEY, merged],
    queryFn: () => listClients(merged),
    placeholderData: (prev) => prev,
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'detail', id],
    queryFn: () => getClient(id as string),
    enabled: Boolean(id),
  });
}

export function useClientInvoices(id: string | undefined) {
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'invoices', id],
    queryFn: () => getClientInvoices(id as string),
    enabled: Boolean(id),
  });
}

// --- Client mutations ------------------------------------------------------

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientInput) => createClient(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useUpdateClient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ClientInput) => updateClient(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteClient(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useBulkDeleteClients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteClients(ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useSeedClients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => seedClientsFromInvoices(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

// --- Contact mutations -----------------------------------------------------

export function useCreateContact(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContactInput) => createContact(clientId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...CLIENTS_KEY, 'detail', clientId] });
    },
  });
}

export function useUpdateContact(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contactId: string; input: ContactInput }) =>
      updateContact(clientId, vars.contactId, vars.input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...CLIENTS_KEY, 'detail', clientId] });
    },
  });
}

export function useDeleteContact(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => deleteContact(clientId, contactId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...CLIENTS_KEY, 'detail', clientId] });
    },
  });
}

// --- Resource mutations ----------------------------------------------------

export function useCreateResource(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      category: ResourceCategory;
      s3Key: string;
      description?: string;
      fileType?: string;
      fileSize?: number;
    }) => createResource(clientId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...CLIENTS_KEY, 'detail', clientId] });
    },
  });
}

export function useDeleteResource(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (resourceId: string) => deleteResource(clientId, resourceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...CLIENTS_KEY, 'detail', clientId] });
    },
  });
}

// Meeting-note mutations moved to the polymorphic `useMeetingNotes` hooks
// (CLE-205) backed by the unified `/api/meeting-notes` router.
